/**
 * Nova Container Component
 * 
 * A container component for Nova data visualization that includes a configurable header
 * showing query information, filters, and sorting options.
 */

import * as React from 'react';
import { NovaContainerProps } from './types';
import { ColumnRef } from '../data-grid/types';
import { 
  Query,
  QueryTypeEnum,
  TableRef,
  TableSchema,
  TableView,
  getDatasetDisplayName,
  SQLDialect,
  MaterializedTableData,
  AggregateOperationEnum,
  SortDirectionEnum,
  SelectQueryOptions,
  AggregateQueryOptions,
  SQLQueryOptions,
} from '../../../lib/engine';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  ScrollBar,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  Skeleton,
  Progress
} from '../../../components/ui';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../../components/ui/resizable';
import { toast } from '../../../components/ui/toast';
import { ChevronDown, ChevronUp, Code, Filter, SortAsc, SortDesc, GripHorizontal } from 'lucide-react';
import { NovaHeader } from './nova-header/nova-header';
import { formatRowCount } from '../data-grid/cell-formatting';
import { useNovaStoreContext } from '../nova/nova-store-context';
import { SelectQueryConfig } from './nova-header/select-query-config';
import { AggregateQueryConfig } from './nova-header/aggregate-query-config';
import { SQLQueryConfig } from './nova-header/sql-query-config';

// Default sizes for the panels
const DEFAULT_COLLAPSED_SIZE = 0;
const DEFAULT_EXPANDED_SIZE = 25;
const MIN_EXPANDED_SIZE = 20;
const MAX_EXPANDED_SIZE = 70;

/**
 * Nova Container Component
 */
export function NovaContainer({
  children,
  columns,
  originalColumns,
  query,
  updateQuery,
  className,
  dataName,
  rowCount,
  executeQuery,
  topRightSlot,
  lastExecutedView,
  onUpgradeClick
}: NovaContainerProps) {
  // Get the store for UI state management
  const { 
    isQueryBuilderExpanded, 
    lastExpandedSize,
    setLastExpandedSize,
    setQueryBuilderExpanded
  } = useNovaStoreContext();
  
  // Add logging for rowCount
  React.useEffect(() => {
    // Only show warning if we're not in a loading state and originalColumns is still empty
    // During initial load, it's normal for originalColumns to be empty temporarily
    if (originalColumns.length === 0 && rowCount > 0) {
      toast.error('Missing column data', {
        description: 'Original columns data is missing. This will cause issues with aggregation queries.',
        duration: 5000,
        closeButton: true,
      });
    }
  }, [rowCount, columns, originalColumns, dataName]);
  
  // Calculate the current query builder size based on expansion state
  const currentQueryBuilderSize = isQueryBuilderExpanded 
    ? Math.max(lastExpandedSize, MIN_EXPANDED_SIZE) 
    : DEFAULT_COLLAPSED_SIZE;
  
  // Calculate the content size (remaining space)
  const contentSize = 100 - currentQueryBuilderSize;
  
  // Handle panel resize
  const handlePanelResize = (sizes: number[]) => {
    if (sizes && sizes.length > 0 && typeof sizes[0] === 'number') {
      // If the panel is expanded and the user resizes it, remember this size
      if (isQueryBuilderExpanded && sizes[0] >= MIN_EXPANDED_SIZE) {
        setLastExpandedSize(sizes[0]);
      }
    }
  };
  
  // Format rowCount to a number to prevent rendering Float objects directly
  const formattedRowCount = React.useMemo(() => {
    // If it's already a number, use it directly
    if (typeof rowCount === 'number') return rowCount;
    
    // If it's a Float object with a value property, extract the value
    if (typeof rowCount === 'object' && rowCount !== null) {
      // Use type assertion to access the value property
      const rowCountObj = rowCount as { value?: number };
      if (rowCountObj.value !== undefined) {
        return rowCountObj.value;
      }
      
      // Try to convert to number if possible
      try {
        const str = String(rowCount);
        const num = Number(str);
        if (!isNaN(num)) return num;
      } catch (e) {
        // Don't show toast for this internal conversion error
      }
    }
    
    // Default fallback
    return 0;
  }, [rowCount]);
  
  // Check if we're in a loading state (missing required props)
  // Since props are now required, we only need to check for empty arrays
  const isLoading = React.useMemo(() => {
    // Check for empty originalColumns (required for AggregateQueryConfig)
    if (originalColumns.length === 0) {
      // Don't return true here, we'll show a toast instead of blocking the UI
    }
    
    // Check for empty columns (required for all query types)
    if (columns.length === 0) {
      // Don't return true here, we'll show a toast instead of blocking the UI
    }
    
    // Only return true if we're still initializing data
    // This prevents blocking the UI with a loading screen when data is partially available
    return false;
  }, [originalColumns, columns]);

  // For loading animation
  const [loadingProgress, setLoadingProgress] = React.useState(0);
  
  // Animate loading progress
  React.useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          const newValue = prev + 1;
          return newValue > 100 ? 0 : newValue;
        });
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [isLoading]);
  
  // Force re-render of the panel group when expansion state changes
  const [panelKey, setPanelKey] = React.useState(0);
  
  // Update panel key when expansion state changes to force re-render
  React.useEffect(() => {
    setPanelKey(prev => prev + 1);
  }, [isQueryBuilderExpanded]);
  
  // Function to handle selected item click
  const handleSelectedItemClick = () => {
    // Toggle the query builder expansion state
    setQueryBuilderExpanded(!isQueryBuilderExpanded);
  };
  
  // Get the current query type or default to SELECT
  const queryType = query?.type || QueryTypeEnum.SELECT;
  
  return (
    <div className={cn("flex flex-col h-full w-full overflow-hidden", className)}>
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 flex flex-col items-center justify-center z-50">
          <div className="w-64 space-y-4">
            <h3 className="text-lg font-medium text-center">Loading data...</h3>
            <Progress value={loadingProgress} className="w-full" />
          </div>
        </div>
      )}
      
      {/* Fixed Header */}
      <div className="shrink-0 border-b bg-background">
        <div className="px-2 py-2">
          <NovaHeader
            query={query}
            columns={columns}
            originalColumns={originalColumns}
            updateQuery={updateQuery}
            dataName={dataName}
            rowCount={formattedRowCount}
            executeQuery={executeQuery}
            topRightSlot={topRightSlot}
            lastExecutedView={lastExecutedView}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      </div>
      
      {/* Resizable Content Area */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup
          key={panelKey}
          direction="vertical"
          onLayout={handlePanelResize}
          className="h-full"
        >
          {/* Query Builder Panel */}
          {isQueryBuilderExpanded ? (
            <ResizablePanel 
              defaultSize={DEFAULT_EXPANDED_SIZE}
              minSize={MIN_EXPANDED_SIZE}
              maxSize={MAX_EXPANDED_SIZE}
              className="overflow-hidden border-b"
            >
              <div className="p-2 h-full">
                <div className="border rounded-md p-2 bg-background flex flex-col overflow-hidden h-full">
                  {queryType === QueryTypeEnum.SELECT && query && updateQuery && (
                    <ScrollArea className="w-full h-full overflow-auto">
                      <div className="pr-3 pb-2">
                        <div>
                          <SelectQueryConfig 
                            query={query} 
                            originalColumns={originalColumns} 
                            updateQuery={updateQuery} 
                            onSelectedItemClick={handleSelectedItemClick}
                          />
                        </div>
                      </div>
                    </ScrollArea>
                  )}
                  
                  {queryType === QueryTypeEnum.AGGREGATE && query && updateQuery && (
                    <div className="h-full flex flex-col">
                      <div className="flex-1 overflow-hidden">
                        <AggregateQueryConfig 
                          query={query} 
                          columns={columns} 
                          originalColumns={originalColumns} 
                          updateQuery={updateQuery} 
                        />
                      </div>
                    </div>
                  )}
                  
                  {queryType === QueryTypeEnum.SQL && query && updateQuery && (
                    <div className="h-full flex flex-col">
                      <div className="flex-1 overflow-hidden">
                        <SQLQueryConfig 
                          query={query} 
                          updateQuery={updateQuery}
                          columns={columns}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ResizablePanel>
          ) : (
            <div className="border-b" />
          )}
          
          {/* Resize handle - always visible but only draggable when expanded */}
          <ResizableHandle 
            withHandle 
            className={cn(
              "transition-all duration-200",
              !isQueryBuilderExpanded && "pointer-events-none opacity-0"
            )} 
          />
          
          {/* Data Grid Panel */}
          <ResizablePanel defaultSize={contentSize} minSize={20} className="overflow-hidden">
            <div className="h-full overflow-hidden">
              {children}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
} 